import { symphonyAbi, symphonyAddress } from "../../common/constants/contract";
import { encodeFunctionData, isAddress } from "viem";
import { getTotalAmountIn } from "./getTotalAmountIn";
import { getConfig } from "../constants/config";
import { calculateAmountOutMin } from "./calculateAmountOutMin";
import { getTokenIn } from "./getTokenIn";
import { getTokenOut } from "./getTokenOut";

/**
 * Encodes the calldata for a swap transaction on the Symphony protocol without executing it.
 * This is useful for transaction simulation using eth_call.
 *
 * @param {Object} params - Parameters for encoding swap calldata
 * @param {Array} params.route - Route information from getRoute
 * @param {boolean} params.includesNative - Whether the swap includes native token
 * @param {string|Object} params.from - From address or wallet client
 * @param {Object} [params.slippage] - Slippage configuration
 * @param {number} [params.slippage.slippageAmount=config.slippage] - Slippage amount
 * @param {boolean} [params.slippage.isRaw=true] - If true, slippage is in raw units
 * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
 * @param {number} [params.slippage.outTokenDecimals] - Output token decimals
 * @returns {Object} Object containing the encoded calldata and transaction value (if needed)
 *
 * @example
 * const route = await getRoute({
 *   tokenIn: "0x0", // SEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const tx = await generateCalldata({
 *   route,
 *   includesNative: true,
 *   from: "0xa2a9dd657D44e46E2d1843B8784eFc3dE3Cf3A57"
 * });
 */
export const generateCalldata = async ({
  from,
  route,
  includesNative,
  options = {},
  slippage = {
    slippageAmount: getConfig().slippage,
    isBps: false,
  },
}) => {
  try {
    let fromAddress;
    if (typeof from === "string" && isAddress(from)) {
      // Check if the string is a valid Ethereum address
      fromAddress = from;
    } else if (from && typeof from.account?.address === "string") {
      // If it's a wallet client, get the address from account
      fromAddress = from.account.address;
    } else {
      throw new Error(
        "Invalid 'from' parameter: must be a valid Ethereum address or a wallet client"
      );
    }

    const { nativeAddress, wrappedNativeAddress, functionName, feeParams } =
      getConfig();
    let tokenIn = getTokenIn(route);
    let tokenOut = getTokenOut(route);

    // If native currency included, treat wrapped currency as native
    includesNative
      ? tokenIn === wrappedNativeAddress
        ? (tokenIn = nativeAddress)
        : (tokenOut = nativeAddress)
      : null;

    // Calculate minimum output amount based on slippage
    const amountOutMin = await calculateAmountOutMin({
      variable: route,
      slippageAmount: slippage.slippageAmount,
      options: {
        isBps: slippage.isBps ?? false,
      },
    });

    let data;
    let value = "0";

    // NATIVE TO TOKEN TRANSACTION
    if (tokenIn === nativeAddress && tokenOut !== nativeAddress) {
      data = encodeFunctionData({
        abi: symphonyAbi,
        functionName,
        args: [
          route,
          amountOutMin,
          false,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
      });
      value = (await getTotalAmountIn(route)).amountIn.toString();
    }
    // TOKEN TO NATIVE TRANSACTION
    else if (tokenIn !== wrappedNativeAddress && tokenOut === nativeAddress) {
      data = encodeFunctionData({
        abi: symphonyAbi,
        functionName,
        args: [
          route,
          amountOutMin,
          true,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
      });
    }
    // TOKEN TO TOKEN TRANSACTION
    else if (tokenIn !== nativeAddress && tokenOut !== nativeAddress) {
      data = encodeFunctionData({
        abi: symphonyAbi,
        functionName,
        args: [
          route,
          amountOutMin,
          false,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
      });
    } else {
      throw new Error("Invalid token combination for swap");
    }

    return {
      account: fromAddress,
      to: symphonyAddress,
      data,
      value,
    };
  } catch (error) {
    console.error("Failed to encode swap calldata:", error);
    throw error;
  }
};
